import {Injectable, OnDestroy} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {AuthenticationService} from '../authentication.service';
import {filter, take, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {isBoolean} from 'lodash';
import {XeroConnectionService} from '../xero-connection.service';
import {DialogService} from '../dialog.service';
import {IntercomUserService} from '../intercom-user.service';
import {IntercomUserSelectorModalComponent} from '../../components/intercom-user-selector-modal/intercom-user-selector-modal.component';

@Injectable({
    providedIn: 'root'
})
export class LoggedInGuard implements OnDestroy {

    subDestroyer: Subject<void>;

    constructor(
        private authService: AuthenticationService,
        private xeroConnectionService: XeroConnectionService,
        private dialogService: DialogService,
        private intercomUserService: IntercomUserService
    ) {
        this.subDestroyer = new Subject<void>();
    }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Promise<boolean> {

        if (!!next.queryParams.at) {
            const at = next.queryParams.at;
            this.xeroConnectionService.saveToken(at);
        }

        if (state.url) {
            const managingPassword = state.url.startsWith('/setup/passwordreset') || state.url.startsWith('/setup/update-password');

            if (state.url.startsWith('/setup/update-password')) {
                const resetCode = state.root.queryParamMap.get('oobCode');
                this.authService.setResetPasswordCode(resetCode);
            }

            if (managingPassword) {
                return new Promise<boolean>((resolve) => {
                    resolve(true);
                });
            }
        }

        return new Promise<boolean>((res) => {
            this.authService.isLoggedIn$()
                .pipe(
                    takeUntil(this.subDestroyer),
                    filter((res) => isBoolean(res)),
                    take(1)
                )
                .subscribe((isLoggedIn) => {
                    if (!!state.url) {
                        // Check if user is rise user and needs to select intercom identity
                        if (this.intercomUserService.isRiseUser(this.authService.currentUserEmail)) {
                            const selectedUserId = this.intercomUserService.getSelectedUserId();
                            const selectedUserName = this.intercomUserService.getSelectedUserName();
                            
                            if (!selectedUserId && !this.intercomUserService.hasShownModal()) {
                                // Show user selector modal only once per session
                                this.intercomUserService.setModalShown();
                                const dialogRef = this.dialogService.openDialog(
                                    IntercomUserSelectorModalComponent,
                                    {},
                                    {
                                        disableClose: true,
                                        width: '500px',
                                        panelClass: 'intercom-user-selector-dialog'
                                    }
                                );
                                
                                dialogRef.afterClosed().subscribe((selectedUser) => {
                                    if (selectedUser) {
                                        window.Intercom('update', {
                                            user_id: selectedUser.id,
                                            email: selectedUser.id + '@rise.com',
                                            name: selectedUser.name
                                        });
                                    } else {
                                        window.Intercom('update', {email: this.authService.currentUserEmail});
                                    }
                                });
                            } else {
                                // Use previously selected user
                                window.Intercom('update', {
                                    user_id: selectedUserId,
                                    email: selectedUserId + '@rise.com',
                                    name: selectedUserName
                                });
                            }
                        } else {
                            // Normal user - use their actual email
                            window.Intercom('update', {email: this.authService.currentUserEmail});
                        }
                        
                        res(isLoggedIn
                            || state.url.startsWith('/agent')
                            || state.url.startsWith('/users/enable-contractor'));
                        return;
                    }
                    res(isLoggedIn);
                    return;
                }, (err) => {
                    console.error(err);
                    res(false);
                });
        });
    }

    ngOnDestroy() {
        this.subDestroyer.next();
    }
}
